import React, { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import Page from '../../../components/Page';
import Breadcrumb from '../../../components/Breadcrumb';
import client from '../../../lib/feathers';

const PAGE_SIZE = 50;

const OUTCOMES = ['ok', 'partial', 'failed', 'skipped', 'running', 'claimed', 'pending', 'awaiting_review'];
const TRIGGERS = ['orchestrator', 'manual', 'c7_autotrigger', 'new_listing_hook', 'rescan'];

const OUTCOME_STYLE = {
  ok:               'bg-green-100 text-green-800',
  partial:          'bg-amber-100 text-amber-800',
  failed:           'bg-red-100 text-red-800',
  skipped:          'bg-gray-100 text-gray-800',
  running:          'bg-blue-100 text-blue-800',
  pending:          'bg-purple-100 text-purple-800',
  awaiting_review:  'bg-amber-100 text-amber-900',
  claimed:          'bg-blue-100 text-blue-800',
};

const TRIGGER_LABEL = {
  orchestrator:     'Orchestrator',
  manual:           'Manual',
  c7_autotrigger:   'Commerce7',
  new_listing_hook: 'New-listing',
  rescan:           'Rescan',
};

function OutcomePill({ outcome }) {
  return (
    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${OUTCOME_STYLE[outcome] || 'bg-gray-100 text-gray-800'}`}>
      {outcome}
    </span>
  );
}

function durationLabel(startedAt, finishedAt) {
  if (!startedAt || !finishedAt) return null;
  const ms = new Date(finishedAt) - new Date(startedAt);
  if (ms < 1000) return `${ms}ms`;
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return rem ? `${min}m ${rem}s` : `${min}m`;
}

function StageSummary({ stages }) {
  if (!stages || typeof stages !== 'object') return <span className="text-xs text-gray-400">—</span>;
  const entries = Object.entries(stages);
  const ok = entries.filter(([, s]) => s?.ok === true).length;
  const failed = entries.filter(([, s]) => s?.ok === false).length;
  return (
    <span className="text-xs font-mono text-gray-600" title={entries.map(([k, s]) => `${k}: ${s?.ok === true ? 'ok' : s?.ok === false ? 'fail' : '?'}`).join('\n')}>
      <span className="text-green-700">{ok}</span>
      <span className="text-gray-400"> / </span>
      <span className="text-red-700">{failed}</span>
      <span className="text-gray-400"> / {entries.length}</span>
    </span>
  );
}

export default function ListingDataHealth() {
  const [runs, setRuns] = useState(null);
  const [page, setPage] = useState(0);
  const [outcome, setOutcome] = useState('');
  const [triggeredBy, setTriggeredBy] = useState('');
  const [error, setError] = useState(null);
  const [responseTotal, setResponseTotal] = useState(0);
  // What the background sweep would pick next, from the picker
  // endpoint. Refreshed on mount + after each load.
  const [sweepCandidates, setSweepCandidates] = useState(null);
  // How many runs are sitting in awaiting_review without an approval.
  // Surfaced as a banner so admin doesn't miss pending work.
  const [pendingApprovalCount, setPendingApprovalCount] = useState(null);
  // Currently-active runs (claimed/running), hoisted to the top of the view and
  // polled so progress is visible regardless of the table filter.
  const [activeRuns, setActiveRuns] = useState(null);

  const query = useMemo(() => {
    const q = { $limit: PAGE_SIZE, $skip: page * PAGE_SIZE };
    // "In progress" = a chain is actively working. The supervisor claims a row
    // (outcome='claimed') and the worker flips it to 'running', so filtering to
    // 'running' alone shows empty between/around chains even while the queue is
    // healthy. Treat claimed+running as one "in progress" filter.
    if (outcome === '__in_progress__') q.outcome = { $in: ['claimed', 'running'] };
    else if (outcome) q.outcome = outcome;
    if (triggeredBy) q.triggeredBy = triggeredBy;
    return q;
  }, [page, outcome, triggeredBy]);

  const load = async () => {
    try {
      setRuns(null);
      const result = await client.service('admin/listing-enrichment-run').find({ query });
      setRuns(result.data || []);
      setResponseTotal(result.total ?? (result.data || []).length);
      setError(null);
    } catch (e) {
      console.error('listing-data-health load error', e);
      setError(e.message || 'Failed to load enrichment runs');
    }
  };

  useEffect(() => {
    load();
  }, [query]);

  // Sweep-candidates preview — what the supervisor would pick next.
  // Independent of the main filtered table.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await client
          .service('admin/listing-enrichment-sweep-candidates')
          .find({ query: { $limit: 8 } });
        if (!cancelled) setSweepCandidates(result.data || []);
      } catch (e) {
        if (!cancelled) setSweepCandidates([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Pending-approval count — surfaces "you have N runs to review"
  // banner near the top.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Want the count of rows that ACTUALLY need a human's eyes —
        // not approved-and-waiting-for-supervisor ones. Can't filter
        // `approvedAt IS NULL` via REST (string "null" → timestamp
        // coercion 500). Fetch a page of awaiting_review rows and
        // count `!approvedAt` client-side. Capped at 200 — if more
        // than that exist, the banner's number is approximate but
        // the click-through report uses the same filter so the lists
        // match.
        const result = await client.service('admin/listing-enrichment-run').find({
          query: { outcome: 'awaiting_review', $limit: 200 },
        });
        const unapprovedCount = (result.data || []).filter((r) => !r.approvedAt).length;
        if (!cancelled) setPendingApprovalCount(unapprovedCount);
      } catch (e) {
        if (!cancelled) setPendingApprovalCount(0);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Active work — hoisted + polled so it's always visible regardless of the
  // table filter. Two groups: (a) in-flight chains (claimed/running) and
  // (b) approved runs WAITING for an execute slot (outcome=awaiting_review with
  // approvedAt set) — these were previously invisible (neither running nor in
  // the un-approved pending-approval list), so an approved run looked like it
  // vanished until the supervisor's next free slot picked it up.
  useEffect(() => {
    let cancelled = false;
    const fetchActive = async () => {
      try {
        const [inflightRes, awaitingRes] = await Promise.all([
          client.service('admin/listing-enrichment-run').find({
            query: { outcome: { $in: ['claimed', 'running'] }, $sort: { startedAt: -1 }, $limit: 10 },
          }),
          client.service('admin/listing-enrichment-run').find({
            query: { outcome: 'awaiting_review', $sort: { finishedAt: -1 }, $limit: 50 },
          }),
        ]);
        // approvedAt can't be filtered server-side via REST (null→timestamp 500),
        // so split client-side: approved = queued for execute.
        const approvedWaiting = (awaitingRes.data || []).filter((r) => r.approvedAt);
        if (!cancelled) setActiveRuns({ inflight: inflightRes.data || [], approvedWaiting });
      } catch (e) {
        if (!cancelled) setActiveRuns({ inflight: [], approvedWaiting: [] });
      }
    };
    fetchActive();
    const t = setInterval(fetchActive, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const kpi = useMemo(() => {
    if (!runs || runs.length === 0) return null;
    const ok = runs.filter((r) => r.outcome === 'ok').length;
    const partial = runs.filter((r) => r.outcome === 'partial').length;
    const failed = runs.filter((r) => r.outcome === 'failed').length;
    const running = runs.filter((r) => r.outcome === 'running').length;
    return { ok, partial, failed, running, total: runs.length };
  }, [runs]);

  const totalPages = Math.max(1, Math.ceil(responseTotal / PAGE_SIZE));

  const resetFilters = () => {
    setOutcome('');
    setTriggeredBy('');
    setPage(0);
  };

  return (
    <Page title="Reports">
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[{ name: 'Reports', href: `/admin/reports` }, { name: 'Listing Data Health' }]} />
        </nav>
      </div>

      <div className="py-5">
        <h1 className="text-2xl pb-3">Listing Data Health</h1>
        <p className="text-sm text-gray-500 max-w-2xl">
          Pipeline enrichment runs across all listings. One row per chain
          execution. Use the filters to find failed or stalled runs.
        </p>
      </div>

      {pendingApprovalCount != null && pendingApprovalCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex justify-between items-center">
          <div>
            <span className="font-medium text-amber-900">
              {pendingApprovalCount} run{pendingApprovalCount === 1 ? '' : 's'} awaiting your approval
            </span>
            <span className="text-sm text-amber-800 ml-2">
              — full chains finished their read-only stages and are waiting to write to prod
            </span>
          </div>
          <NavLink
            to="/admin/reports/listing-data-health/pending-approval"
            className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-red-700"
          >
            Review →
          </NavLink>
        </div>
      )}

      {activeRuns && (activeRuns.inflight.length > 0 || activeRuns.approvedWaiting.length > 0) && (
        <div className="bg-white shadow rounded-lg p-4 mb-6 border-l-4 border-blue-400">
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
              Active runs ({activeRuns.inflight.length + activeRuns.approvedWaiting.length})
            </h2>
            <span className="text-xs text-blue-600 animate-pulse">live</span>
          </div>
          <ul className="divide-y divide-gray-100">
            {activeRuns.inflight.map((r) => (
              <li key={r.id} className="py-2 flex items-center justify-between text-sm gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <OutcomePill outcome={r.outcome} />
                  <NavLink to={`/admin/reports/listing-data-health/run/${r.id}`} className="text-indigo-700 hover:underline truncate font-medium">
                    {r.listing?.name || r.id.slice(0, 8)}
                  </NavLink>
                  <span className="text-xs text-gray-400 font-mono">{r.kind}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500 whitespace-nowrap">
                  {r.startedAt && <span>running {formatDistanceToNow(new Date(r.startedAt))}</span>}
                  <NavLink to={`/admin/reports/listing-data-health/run/${r.id}`} className="text-indigo-700 hover:underline">
                    steps →
                  </NavLink>
                </div>
              </li>
            ))}
            {activeRuns.approvedWaiting.map((r) => (
              <li key={r.id} className="py-2 flex items-center justify-between text-sm gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                    approved
                  </span>
                  <NavLink to={`/admin/reports/listing-data-health/run/${r.id}`} className="text-indigo-700 hover:underline truncate font-medium">
                    {r.listing?.name || r.id.slice(0, 8)}
                  </NavLink>
                  <span className="text-xs text-gray-400 font-mono">{r.kind}</span>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500 whitespace-nowrap">
                  <span className="text-green-700">waiting to execute · writes on next free slot</span>
                  <NavLink to={`/admin/reports/listing-data-health/run/${r.id}`} className="text-indigo-700 hover:underline">
                    steps →
                  </NavLink>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {kpi && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs text-gray-500 uppercase tracking-wider">Loaded</div>
            <div className="text-2xl font-semibold text-gray-900">{kpi.total}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs text-green-700 uppercase tracking-wider">Success</div>
            <div className="text-2xl font-semibold text-gray-900">{kpi.ok}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs text-amber-700 uppercase tracking-wider">Partial</div>
            <div className="text-2xl font-semibold text-gray-900">{kpi.partial}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs text-red-700 uppercase tracking-wider">Failed</div>
            <div className="text-2xl font-semibold text-gray-900">{kpi.failed}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs text-blue-700 uppercase tracking-wider">Running</div>
            <div className="text-2xl font-semibold text-gray-900">{kpi.running}</div>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-4 mb-6 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Outcome</label>
          <select
            value={outcome}
            onChange={(e) => { setOutcome(e.target.value); setPage(0); }}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="">All</option>
            <option value="__in_progress__">in progress (claimed+running)</option>
            {OUTCOMES.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Trigger</label>
          <select
            value={triggeredBy}
            onChange={(e) => { setTriggeredBy(e.target.value); setPage(0); }}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="">All</option>
            {TRIGGERS.map((t) => <option key={t} value={t}>{TRIGGER_LABEL[t]}</option>)}
          </select>
        </div>
        {(outcome || triggeredBy) && (
          <button
            type="button"
            onClick={resetFilters}
            className="text-sm text-gray-600 hover:text-gray-900 underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {sweepCandidates && sweepCandidates.length > 0 && (
        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <div className="flex items-baseline justify-between mb-2">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Up next (background sweep)</h2>
            <span className="text-xs text-gray-500 italic">listings the cron will pick next when the queue is empty</span>
          </div>
          <ul className="divide-y divide-gray-100">
            {sweepCandidates.slice(0, 8).map((c) => (
              <li key={c.id} className="py-2 flex justify-between items-baseline text-sm">
                <NavLink to={`/admin/listings/${c.id}`} className="text-indigo-700 hover:underline truncate">
                  {c.name}
                </NavLink>
                <span className="text-xs text-gray-500 whitespace-nowrap ml-3">
                  {c.lastEnrichedAt
                    ? `${c.daysSinceEnrichment ?? '?'}d since ${c.lastOutcome || 'run'}`
                    : 'never enriched'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {error && <div className="p-4 text-sm text-red-700">{error}</div>}
        {!error && runs === null && <div className="p-4 text-sm text-gray-500">Loading…</div>}
        {!error && runs && runs.length === 0 && (
          <div className="p-6 text-sm text-gray-500 italic">No runs match the current filters.</div>
        )}
        {!error && runs && runs.length > 0 && (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">When</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kind</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trigger</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outcome</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stages</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Error</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {runs.map((r) => {
                // Pending rows have startedAt=null (the queue lifecycle added
                // in API v1.83.0). Fall back to createdAt — that's the queue
                // time, which is what makes sense for a pending row.
                const whenTs = r.startedAt || r.createdAt;
                const isPending = !r.startedAt;
                return (
                <tr key={r.id}>
                  <td
                    className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap"
                    title={whenTs ? format(new Date(whenTs), 'PPpp') : ''}
                  >
                    {whenTs ? formatDistanceToNow(new Date(whenTs), { addSuffix: true }) : '—'}
                    {isPending && whenTs && (
                      <span className="text-xs text-gray-400 ml-1">(queued)</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm">
                    {r.listing ? (
                      <NavLink to={`/admin/listings/${r.listing.id}`} className="text-indigo-700 hover:underline">
                        {r.listing.name}
                      </NavLink>
                    ) : (
                      <span className="text-gray-400 italic">unknown</span>
                    )}
                    {r.libationVintage?.libation && (
                      <div className="text-xs text-gray-600 mt-0.5">
                        <NavLink
                          to={`/admin/listings/${r.listing?.id}/wines/${r.libationVintage.libation.id}`}
                          className="text-indigo-700 hover:underline"
                        >
                          {r.libationVintage.libation.name}
                        </NavLink>
                        {r.libationVintage.vintage?.year && (
                          <span className="text-gray-500"> · {r.libationVintage.vintage.year === 9999 ? 'NV' : r.libationVintage.vintage.year}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-600 font-mono">
                    {r.kind}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700">
                    {TRIGGER_LABEL[r.triggeredBy] || r.triggeredBy}
                  </td>
                  <td className="px-4 py-2"><OutcomePill outcome={r.outcome} /></td>
                  <td className="px-4 py-2"><StageSummary stages={r.stages} /></td>
                  <td className="px-4 py-2 text-sm text-gray-700 font-mono text-xs">
                    {durationLabel(r.startedAt, r.finishedAt) || '—'}
                  </td>
                  <td className="px-4 py-2 text-sm text-red-700 max-w-md">
                    {r.error
                      ? <span className="block truncate" title={r.error}>{r.error}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-2 text-sm whitespace-nowrap">
                    <NavLink to={`/admin/reports/listing-data-health/run/${r.id}`} className="text-indigo-700 hover:underline">
                      steps →
                    </NavLink>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm">
            <div className="text-gray-500">
              Page {page + 1} of {totalPages} · {responseTotal} total
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </Page>
  );
}
